// Stylesheet injected into the shadow root. The chat sits on the
// customer's site, so it wears the customer's colors, not ours: the
// ground and the bubbles are neutral, and everything tinted is derived
// from what they configured. The only airys branding left is the
// "Powered by airys" wordmark in the footer.
//
// The loader sets these inline on the host:
//   --airys-primary    bubble and send button
//   --airys-accent     the visitor's own message bubbles
//   --airys-trim*      washes, rings, borders and link colour, computed
//                      from the accent by color.ts
// Their readable foregrounds (--airys-primary-fg / --airys-accent-fg) are
// computed by color.ts, never hardcoded.

export const STYLES = `
:host {
  all: initial;
  position: fixed;
  z-index: 2147483647;
  bottom: 20px;
  font-family: var(--airys-font-body);
  font-size: 15px;
  line-height: 1.5;
  color: var(--airys-ink);
  -webkit-font-smoothing: antialiased;

  --airys-surface: oklch(0.985 0.004 75);
  --airys-ink: #201e1d;
  --airys-white: #fff;
  --airys-danger-100: oklch(0.93 0.03 28);
  --airys-danger-700: oklch(0.43 0.13 28);
  --airys-neutral-50: oklch(0.975 0.008 75);
  --airys-neutral-100: oklch(0.94 0.012 75);
  --airys-neutral-200: oklch(0.88 0.015 75);
  --airys-neutral-300: oklch(0.8 0.018 75);
  --airys-neutral-500: oklch(0.6 0.02 75);
  --airys-neutral-600: oklch(0.52 0.02 75);
  --airys-neutral-700: oklch(0.45 0.02 75);
  --airys-hairline: rgba(32, 30, 29, 0.08);

  --airys-radius-xs: 6px;
  --airys-radius-sm: 8px;
  --airys-radius-control: 12px;
  --airys-radius-card: 16px;
  --airys-radius-pill: 9999px;
  --airys-shadow-soft: 0 1px 3px rgba(60, 45, 20, 0.08);
  --airys-shadow-mid: 0 4px 14px rgba(60, 45, 20, 0.12);
  --airys-shadow-big: 0 10px 30px rgba(60, 45, 20, 0.18);
  /* Ours, and only for the "Powered by airys" wordmark. */
  --airys-font-heading: 'Caprasimo', Georgia, 'Times New Roman', serif;
  --airys-font-body: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;

  /* Customer colors; the loader overrides these inline. A project that
     has configured nothing gets greys, never a colour of ours. */
  --airys-primary: var(--airys-ink);
  --airys-primary-fg: var(--airys-white);
  --airys-primary-soft: rgba(255, 255, 255, 0.16);
  --airys-accent: var(--airys-neutral-700);
  --airys-accent-fg: var(--airys-white);
  /* Trim derived from the accent; these defaults apply until it lands. */
  --airys-trim: var(--airys-neutral-500);
  --airys-trim-wash: var(--airys-neutral-100);
  --airys-trim-ring: var(--airys-neutral-300);
  --airys-trim-text: var(--airys-ink);
}
:host([data-position="bottom_right"]) { right: 20px; align-items: flex-end; }
:host([data-position="bottom_left"]) { left: 20px; align-items: flex-start; }
:host { display: flex; flex-direction: column; }

*, *::before, *::after { box-sizing: border-box; }
button, textarea { font: inherit; color: inherit; }
[hidden] { display: none !important; }
.sr-only {
  position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px;
  overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0;
}
:focus-visible { outline: 2px solid var(--airys-trim); outline-offset: 2px; }

/* ------------------------------ Bubble ------------------------------ */

.bubble {
  width: 56px;
  height: 56px;
  border-radius: var(--airys-radius-pill);
  background: var(--airys-primary);
  color: var(--airys-primary-fg);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: var(--airys-shadow-mid);
  border: 0;
  cursor: pointer;
  transition: transform .2s cubic-bezier(.4, 0, .2, 1), box-shadow .2s;
}
.bubble:hover { transform: translateY(-2px); box-shadow: var(--airys-shadow-big); }
.bubble:active { transform: translateY(0); }
.bubble svg { width: 28px; height: 28px; }

/* ------------------------------ Panel ------------------------------- */

.panel {
  width: 384px;
  max-width: calc(100vw - 40px);
  max-height: calc(100vh - 120px);
  background: var(--airys-surface);
  border: 1px solid var(--airys-hairline);
  border-radius: var(--airys-radius-card);
  box-shadow: var(--airys-shadow-big);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  margin-bottom: 12px;
  height: 640px;
  transform-origin: bottom right;
  animation: airys-enter .25s cubic-bezier(.4, 0, .2, 1);
}
:host([data-position="bottom_left"]) .panel { transform-origin: bottom left; }
@keyframes airys-enter {
  from { opacity: 0; transform: translateY(12px) scale(.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}

/* ------------------------------ Header ------------------------------ */

.header {
  background: var(--airys-primary);
  color: var(--airys-primary-fg);
  border-bottom: 1px solid var(--airys-hairline);
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 12px 12px 20px;
}
.header-left { display: flex; align-items: center; gap: 12px; min-width: 0; flex: 1; }
.header-text { display: flex; flex-direction: column; min-width: 0; }
/* The logo as uploaded, in a 36px box and nothing else.
   A grey plate behind it showed through the anti-aliased edges of a square
   logo as a pale rim, and was the whole background of a transparent one — a
   light tile on a dark header that belongs to no brand. object-fit: cover
   also cropped anything that was not square. */
.header-logo {
  width: 36px;
  height: 36px;
  object-fit: contain;
  background: transparent;
  border-radius: 0;
  flex: none;
}
/* The demo disclaimer. Neutral rather than the brand colour: it is airys
   speaking on a page dressed as someone else, and it has to stay legible
   whatever primary the account picked. Sits between the header and the
   transcript, so it is on screen for as long as the panel is — including
   full screen on a phone, where the panel is the whole page. */
.demo-note {
  background: var(--airys-neutral-100);
  color: var(--airys-ink);
  border-bottom: 1px solid var(--airys-hairline);
  padding: 8px 20px;
  font-size: 12px;
  line-height: 1.4;
  flex: none;
}

.header-title {
  font-weight: 700;
  font-size: 19px;
  line-height: 1.25;
  letter-spacing: 0.01em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.header-text .badge { align-self: flex-start; margin-top: 3px; }
.header-actions { display: flex; gap: 4px; flex: none; }
.header-btn {
  background: transparent;
  border: 0;
  color: var(--airys-neutral-700);
  width: 36px;
  height: 36px;
  border-radius: var(--airys-radius-pill);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  transition: background .15s, color .15s;
}
.header-btn:hover { background: var(--airys-trim-wash); color: var(--airys-trim-text); }
.header .header-btn { color: inherit; }
.header .header-btn:hover { background: var(--airys-primary-soft); color: inherit; }
.header .badge { background: var(--airys-primary-soft); color: inherit; }
.header .badge::before { opacity: 0.8; }
.header-btn svg { width: 20px; height: 20px; }
.header-back { margin-left: -6px; }

/* --------------------- Kit primitives (UiBadge, UiButton) ------------- */

.badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: var(--airys-radius-pill);
  padding: 2px 10px;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
}
.badge::before { content: ''; width: 6px; height: 6px; border-radius: var(--airys-radius-pill); background: currentColor; opacity: 0.7; }
.badge-neutral { background: var(--airys-neutral-100); color: var(--airys-neutral-700); }
.badge-success { background: var(--airys-trim-wash); color: var(--airys-trim-text); }
.badge-accent { background: var(--airys-trim-wash); color: var(--airys-trim-text); }

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 36px;
  padding: 0 16px;
  border-radius: var(--airys-radius-pill);
  border: 1px solid transparent;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: background .12s, border-color .12s, opacity .12s;
}
.btn svg { width: 18px; height: 18px; }
.btn:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-primary { background: var(--airys-primary); color: var(--airys-primary-fg); }
.btn-primary:hover:not(:disabled) { filter: brightness(0.92); }
.btn-secondary { background: var(--airys-white); color: var(--airys-trim-text); border-color: var(--airys-neutral-300); }
.btn-secondary:hover:not(:disabled) { background: var(--airys-trim-wash); border-color: var(--airys-trim-ring); }

/* ---------------------------- Transcript ---------------------------- */

.transcript {
  flex: 1;
  overflow-y: auto;
  padding: 16px 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  scrollbar-width: thin;
  scrollbar-color: var(--airys-neutral-300) transparent;
}
.transcript::-webkit-scrollbar { width: 6px; }
.transcript::-webkit-scrollbar-thumb { background: var(--airys-neutral-300); border-radius: 3px; }

.row { display: flex; flex-direction: column; max-width: 100%; }
.row.user { align-items: flex-end; }
.row.assistant { align-items: flex-start; }

.msg {
  max-width: 85%;
  padding: 10px 14px 11px;
  border-radius: var(--airys-radius-card);
  font-size: 15px;
  line-height: 1.45;
  overflow-wrap: anywhere;
}
.msg-label { font-size: 13px; font-weight: 700; margin-bottom: 2px; }
.msg-assistant { background: var(--airys-white); color: var(--airys-ink); border: 1px solid var(--airys-neutral-200); }
.msg-assistant .msg-label { color: var(--airys-neutral-700); }
.msg-agent { background: var(--airys-white); color: var(--airys-ink); box-shadow: var(--airys-shadow-soft); }
.msg-agent .msg-label { color: var(--airys-neutral-700); }
.msg-user { background: var(--airys-accent); color: var(--airys-accent-fg); }
.msg-user .msg-label { opacity: 0.85; }
.msg-time { font-size: 13px; color: var(--airys-neutral-600); padding: 3px 4px 0; }
.msg-image { display: block; margin-top: 8px; }
.msg-image img, img.msg-image {
  display: block;
  max-width: 240px;
  max-height: 240px;
  border-radius: var(--airys-radius-sm);
  object-fit: cover;
}
.msg-file {
  display: inline-block;
  margin-top: 8px;
  padding: 4px 10px;
  border-radius: var(--airys-radius-pill);
  background: rgba(255, 255, 255, 0.5);
  font-size: 13px;
  color: inherit;
}
.msg-pending { opacity: 0.6; }
.msg a { color: inherit; text-decoration: underline; text-underline-offset: 2px; }

.day-divider {
  align-self: center;
  margin: 2px 0;
  font-size: 13px;
  font-weight: 600;
  color: var(--airys-neutral-600);
}
.system-event {
  align-self: center;
  max-width: 85%;
  margin: 2px 0;
  padding: 6px 14px;
  border-radius: var(--airys-radius-card);
  background: var(--airys-neutral-100);
  font-size: 13px;
  color: var(--airys-neutral-700);
  text-align: center;
}
.notice { background: transparent; color: var(--airys-neutral-600); padding: 2px 12px; }

.citations { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; max-width: 85%; }
.citation {
  font-size: 13px;
  font-weight: 500;
  color: var(--airys-trim-text);
  background: var(--airys-white);
  border: 1px solid var(--airys-neutral-300);
  border-radius: var(--airys-radius-pill);
  padding: 3px 10px;
  text-decoration: none;
  max-width: 220px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: border-color .15s, background .15s;
}
a.citation:hover { border-color: var(--airys-trim); background: var(--airys-trim-wash); }

/* ---------------------------- Welcome ------------------------------- */

.welcome {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 24px 16px;
  gap: 6px;
}
.welcome-icon {
  width: 56px;
  height: 56px;
  margin-bottom: 10px;
  border-radius: var(--airys-radius-pill);
  background: var(--airys-trim-wash);
  color: var(--airys-trim-text);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.welcome-icon svg { width: 28px; height: 28px; }
.welcome-title {
  font-weight: 700;
  font-size: 20px;
  line-height: 1.3;
  color: var(--airys-ink);
  margin: 0;
  max-width: 280px;
  text-wrap: pretty;
}

/* ---------------------------- Typing -------------------------------- */

.typing {
  align-self: flex-start;
  background: var(--airys-white);
  border: 1px solid var(--airys-neutral-200);
  border-radius: var(--airys-radius-card);
  padding: 14px 16px;
  display: inline-flex;
  gap: 4px;
  align-items: center;
}
.typing span {
  width: 7px;
  height: 7px;
  border-radius: var(--airys-radius-pill);
  background: var(--airys-neutral-500);
  animation: airys-blink 1.3s infinite;
}
.typing span:nth-child(2) { animation-delay: .18s; }
.typing span:nth-child(3) { animation-delay: .36s; }
@keyframes airys-blink {
  0%, 80%, 100% { opacity: 0.25; transform: scale(0.9); }
  40% { opacity: 1; transform: scale(1); }
}

/* ------------------------- Resolved + rating ------------------------ */

.csat-card {
  align-self: stretch;
  margin-top: 8px;
  padding: 14px 16px;
  background: var(--airys-white);
  border: 1px solid var(--airys-hairline);
  border-radius: var(--airys-radius-card);
  box-shadow: var(--airys-shadow-soft);
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;
}
.csat-q, .csat-done { font-size: 15px; font-weight: 600; color: var(--airys-ink); }
.csat-row { display: flex; gap: 8px; }
.resolved-actions { display: flex; flex-direction: column; align-items: center; margin-top: 4px; }
/* ---------------------------- History ------------------------------- */

.history-list { display: flex; flex-direction: column; gap: 8px; }
.history-empty { font-size: 14px; color: var(--airys-neutral-600); padding: 8px 4px; }
.history-row {
  text-align: left;
  background: var(--airys-white);
  border: 1px solid var(--airys-hairline);
  box-shadow: var(--airys-shadow-soft);
  border-radius: var(--airys-radius-card);
  padding: 12px 14px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 4px;
  transition: border-color .12s;
}
.history-row:hover { border-color: var(--airys-trim); }
.history-row-preview { font-size: 15px; color: var(--airys-ink); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.history-row-meta { font-size: 13px; color: var(--airys-neutral-600); }

/* ---------------------------- Composer ------------------------------ */

.identity {
  padding: 12px 16px 4px;
  border-top: 1px solid var(--airys-hairline);
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.identity-intro { margin: 0; font-size: 14px; color: var(--airys-neutral-700); }
.identity-field { display: flex; flex-direction: column; gap: 4px; }
.identity-label { font-size: 13px; font-weight: 600; color: var(--airys-ink); }
.identity input {
  font: inherit;
  font-size: 15px;
  color: var(--airys-ink);
  background: var(--airys-white);
  border: 1px solid var(--airys-neutral-300);
  border-radius: var(--airys-radius-control);
  padding: 9px 12px;
  transition: border-color .15s, box-shadow .15s;
}
.identity input:hover { border-color: var(--airys-neutral-500); }
.identity input:focus-visible {
  outline: none;
  border-color: var(--airys-trim);
  box-shadow: 0 0 0 3px var(--airys-trim-ring);
}
.identity-error { font-size: 13px; color: var(--airys-danger-700); }
.identity-submit { align-self: stretch; justify-content: center; }

.composer {
  padding: 12px 16px 4px;
  border-top: 1px solid var(--airys-hairline);
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.composer-input-wrap {
  background: var(--airys-white);
  border: 1px solid var(--airys-neutral-300);
  border-radius: var(--airys-radius-control);
  padding: 8px 14px;
  transition: border-color .15s, box-shadow .15s;
}
.composer-input-wrap:hover { border-color: var(--airys-neutral-500); }
.composer-input-wrap:focus-within {
  border-color: var(--airys-trim);
  box-shadow: 0 0 0 3px var(--airys-trim-ring);
}
.composer textarea {
  display: block;
  width: 100%;
  font-size: 15px;
  line-height: 1.45;
  border: 0;
  background: transparent;
  padding: 4px 0;
  resize: none;
  outline: none;
  color: var(--airys-ink);
  min-height: 22px;
  max-height: 120px;
}
.composer textarea::placeholder { color: var(--airys-neutral-600); }
.composer-row { display: flex; align-items: flex-end; gap: 6px; }
.composer-row textarea { flex: 1; }
.composer-actions { display: flex; align-items: center; gap: 2px; padding-bottom: 2px; }
.composer-btn {
  width: 32px;
  height: 32px;
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: var(--airys-radius-pill);
  background: transparent;
  color: var(--airys-neutral-600);
  cursor: pointer;
  transition: background-color .15s, color .15s;
}
.composer-btn svg { width: 18px; height: 18px; }
.composer-btn:hover:not(:disabled) { background: var(--airys-trim-wash); color: var(--airys-trim-text); }
.composer-btn:disabled { opacity: 0.45; cursor: not-allowed; }
.send { background: var(--airys-primary); color: var(--airys-primary-fg); }
.send:hover:not(:disabled) { background: var(--airys-primary); color: var(--airys-primary-fg); filter: brightness(0.92); }
.attachment-chip { padding-bottom: 8px; }
.attachment-chip-inner {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 6px 4px 4px;
  border-radius: var(--airys-radius-sm);
  background: var(--airys-neutral-100);
  max-width: 100%;
}
.attachment-chip-inner img { width: 36px; height: 36px; border-radius: var(--airys-radius-xs); object-fit: cover; }
.attachment-name { font-size: 13px; color: var(--airys-neutral-700); max-width: 180px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.attachment-remove {
  width: 24px; height: 24px; border: 0; border-radius: var(--airys-radius-pill);
  background: transparent; color: var(--airys-neutral-700); cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
}
.attachment-remove:hover { background: var(--airys-trim-wash); color: var(--airys-trim-text); }
.attachment-remove svg { width: 14px; height: 14px; }
.send { opacity: 0.5; }
.send.is-ready { opacity: 1; }

.composer-footer {
  padding: 4px 20px 12px;
  font-size: 13px;
  color: var(--airys-neutral-600);
  text-align: center;
}
.composer-footer a { color: inherit; text-decoration: none; font-family: var(--airys-font-heading); font-size: 14px; }
.composer-footer a:hover { color: var(--airys-ink); }

/* ----------------------------- Error -------------------------------- */

.error {
  margin: 0 16px 8px;
  padding: 10px 14px;
  font-size: 14px;
  color: var(--airys-danger-700);
  background: var(--airys-danger-100);
  border-radius: var(--airys-radius-control);
}

/* ------------------------- Reduced motion --------------------------- */

@media (prefers-reduced-motion: reduce) {
  .panel { animation: none; }
  .bubble, .bubble:hover { transition: none; transform: none; }
  .typing span { animation: none; opacity: 0.6; }
}

/* ---------------------------- Responsive ---------------------------- */

@media (max-width: 480px) {
  /* iOS Safari zooms the whole page in when a field under 16px takes focus,
     and never zooms back out — so tapping the message box left the customer's
     site magnified. 16px is the threshold, not a preference.
     Every field, not just today's: the next one added would hit this too.
     A viewport maximum-scale would also stop it, and is not an option: it
     blocks pinch-to-zoom, and this runs on pages whose viewport tag is not
     ours to set. */
  :host textarea,
  :host input {
    font-size: 16px;
  }
  /* Holding the previous heights: the composer grew 2px a line at 16px, and
     the identity fields 2px each. */
  .composer textarea { line-height: 1.35; }
  .identity input { padding: 8px 12px; }

  /* The host stays corner-anchored so the bubble keeps honouring
     data-position; the panel goes full-screen on its own. */
  :host(:not([data-preview])) { bottom: 16px; right: 16px; left: auto; }
  :host(:not([data-preview])[data-position="bottom_left"]) { left: 16px; right: auto; }
  :host(:not([data-preview])) .panel {
    position: fixed;
    inset: 0;
    width: 100vw;
    max-width: 100vw;
    height: 100vh;
    height: 100dvh;
    max-height: none;
    border-radius: 0;
    border: 0;
    margin: 0;
  }
}
`
